import { getTimeWithOffset } from '@dispatch/shared';
import {
  Actions,
  AttributeDefinition,
  AttributeDefinitionValue,
  BlobAttribute,
  Cat,
  Company,
  isUnassignedNaqSurface,
  MultiSelectOption,
  ReassignFormBody,
  Resource,
  ResourceMeta,
  ResultStatus,
  Subjects,
  TaskAssignmentOrEvent,
  TaskCancelRequest,
  TaskEvent,
  TaskForm,
  TaskPeril,
  TaskResponse,
  TaskType,
} from '@dispatch/shared/types';
import { Ability } from '@casl/ability';
import {
  DrawerComponent,
  DrawerComponentMeta,
  DrawerMeta,
  Form,
  NotesPanel,
  RenderFuncProps,
  TabContainer,
  TabPanel,
  SelectField,
  ConfirmModal,
  DrawerButton,
  ManualUnassignmentChip,
} from '@dispatch/web/ui';
import { useCurrentUser } from '@dispatch/web/auth';
import { addMinutes, format, parseISO } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { Control, FieldErrors, FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { AssignmentInformation } from './components/AssignmentInformation';
import { GeneralInformation } from './components/GeneralInformation';
import { formDefaults, formCancelDefaults } from './FormDefaults';
import {
  Box,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Button,
  Fab,
  Typography,
  Tooltip,
} from '@mui/material';
import { StyledFieldSet } from './TaskDetailsStyles';
import { isNil } from 'lodash';
import { TaskTimelineTab } from './components/Timeline/Timeline';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';
import { TaskDetailsContext } from '@dispatch/web/hooks';
import { useCanReadNotes, useCompany } from '@dispatch/shared/hooks';
import { MethodOfInspectionChip } from '../list/components/MethodOfInspectionChip';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentPasteGoOutlinedIcon from '@mui/icons-material/ContentPasteGoOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import { grey, indigo } from '@mui/material/colors';
import { GeoStatusChip } from '../shared/GeoStatusChip';
import { SupplementalDataTab } from './components/SupplementalData/SupplementalData';
import { Link } from 'react-router-dom';
import { TaskAssignedChip } from './components';
import { TaskNAQChip } from './components/TaskNAQChip';
import { TaskRetryButton } from './components/TaskRetryButton';
import { TimeInNaqIndicator } from './components/TimeInNaqIndicator';
import { useMarkUnassign } from './components/UseMarkUnassign';

export type TaskDetailsProps = {
  data?: TaskResponse;
  status: ResultStatus;
  onSave: (value: any) => void;
  onCancelTask: (value: string) => void;
  onLockTask: () => void;
  navigateToOnClose?: string;
  onStartTask: () => void;
  onCompleteTask: () => void;
  isSaving?: boolean;
  resources: any;
  permissions: Ability<[Actions, Subjects]> | undefined;
  currentTab: number;
  setTab: (newTab: number) => void;
  perils: any;
  showOnlyQualified: {
    value: boolean;
    setShowOnlyQualified: React.Dispatch<boolean>;
  };
  taskEvents?: TaskAssignmentOrEvent[];
  getTaskEventsStatus: ResultStatus;
  rescheduleReasons?: MultiSelectOption[];
  notifyError: (
    title: string,
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey;
  resourceMeta?: ResourceMeta;
  disableButtons?: boolean;
  isAdjuster?: boolean;
  geoStatus?: string;
  cancelStatus?: ResultStatus;
  tabLabels?: string[];
  isInternalAdmin?: boolean;
  onForceReassign?: (value: string, reasonCode: string) => void;
  company?: Company;
  attributes?:
    | {
        definition: AttributeDefinitionValue | undefined;
        definitionParent: AttributeDefinition | undefined;
        id: string;
        cat: Cat;
        nonCat: Cat;
        useProficiency: boolean;
        minimumProficiency: number;
        isPrepopulated: boolean;
      }[]
    | undefined;
  assignedResource?: Resource | undefined;
  latestNaqEvent?: TaskEvent;
  handleUnassignTask?: (taskId: string) => void;
};

const TaskValidationSchema = yup.object().shape({
  resourceId: yup.string().nullable(),
  needsReason: yup.boolean().nullable(),
  reasonCode: yup.string().when('needsReason', {
    is: true,
    then: yup.string().required('Must specify a reschedule reason'),
  }),
  assignmentHandling: yup.object().shape({
    appointmentStart: yup
      .date()
      .test(
        'appointment_start_window',
        'Cannot move task start time from the initially scheduled window.',
        (value, context) => {
          const assignmentHandling = context.parent;
          if (!value) return true;
          return (
            value >= assignmentHandling.mustStartAfterTime &&
            value < assignmentHandling.mustEndBeforeTime
          );
        }
      ),
  }),
});

const taskCancelValidationSchema = yup.object().shape({
  cancelReason: yup.string().required('Must specify a cancel reason'),
});

const STARTED_QUEUE = 'wip_q';
const COMPLETED_QUEUE = 'egress';
const CANCELLED_QUEUE = 'cancelled_q';

export function TaskDetails({
  data,
  onSave,
  onForceReassign,
  onCancelTask,
  onLockTask,
  onStartTask,
  onCompleteTask,
  status,
  isSaving = false,
  resources,
  permissions,
  currentTab,
  setTab,
  perils,
  showOnlyQualified,
  taskEvents,
  getTaskEventsStatus,
  notifyError,
  resourceMeta,
  rescheduleReasons,
  disableButtons,
  isAdjuster,
  geoStatus,
  cancelStatus,
  tabLabels: tabLabelsProp,
  isInternalAdmin,
  company,
  attributes,
  assignedResource,
  latestNaqEvent,
  handleUnassignTask,
  navigateToOnClose = '/tasks',
}: TaskDetailsProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const params = useParams();
  const taskId = params['taskId'];
  const navigate = useNavigate();
  const { tenantId } = useCurrentUser();
  const canReadNotes = useCanReadNotes();

  // Drop the NOTES tab when the user has no notes permission so the tab
  // strip doesn't advertise a section they can't open. The conditional
  // doesn't add NOTES — it removes it from whatever the caller passed in
  // (or from the default).
  const tabLabels = useMemo<string[]>(() => {
    const raw = tabLabelsProp ?? ['GENERAL', 'LOCATIONS', 'NOTES', 'TIMELINE'];
    return canReadNotes ? raw : raw.filter((l) => l !== 'NOTES');
  }, [tabLabelsProp, canReadNotes]);
  const notesTabIndex = tabLabels.indexOf('NOTES');
  const timelineTabIndex = tabLabels.indexOf('TIMELINE');
  const supplementalTabIndex = tabLabels.indexOf('SUPPLEMENTAL DATA');

  const canEdit = useMemo(() => {
    if (
      data?.$msg.taskType === 'COMPLEX' ||
      data?.$msg.taskType === 'COMPLEX_CHILD'
    ) {
      return permissions?.can('upsert', 'complex-loss');
    }

    return permissions?.can?.('update', 'task');
  }, [data?.$msg.taskType, permissions]);

  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: { ...formDefaults },
    resolver: yupResolver(TaskValidationSchema),
  });

  const notifyValidationErrors = () => {
    if (!isNil(formErrors)) {
      const errors = Object.entries(formErrors);
      if (errors.length > 0) {
        notifyError(
          'Error',
          (errors[0] as any)?.[1]?.message ??
            'Please make sure all fields are valid'
        );
      }
    }
  };

  React.useEffect(() => {
    notifyValidationErrors();
  }, [formErrors]);

  const {
    control: formCancelControl,
    formState: formCancelState,
    handleSubmit: handleCancelSubmit,
    reset: resetCancel,
    watch,
  } = useForm({
    defaultValues: { ...formCancelDefaults },
    resolver: yupResolver(taskCancelValidationSchema),
  });

  const { cancelReasons, cancelReasonsStatus, featureFlags } = useCompany();

  const parseTaskDataForForm: any = React.useMemo(() => {
    if (!data) return;

    const startDateTime = new Date(
      data?.$msg?.assignmentHandling?.appointment || ''
    ).toJSON();
    const timeZoneId = data?.$msg?.claim?.lossLocation?.timeZoneId || 'unknown';
    const durationInMinutes = Math.trunc(
      (data?.$msg?.assignmentHandling?.durationInMinutes || 0) *
        (1 / (data?.$metadata?.appliedEfficiencyCoefficient || 1))
    );

    const startTime = !isNil(startDateTime)
      ? getTimeWithOffset(timeZoneId, startDateTime)
      : null;
    const endtime =
      startTime && !isNil(startTime)
        ? addMinutes(new Date(startTime), durationInMinutes)
        : null;

    const mustStartAfterTime = parseISO(
      data?.$msg?.assignmentHandling?.mustStartAfter
    );
    const mustEndBeforeTime = parseISO(
      data?.$msg?.assignmentHandling?.mustEndBefore
    );
    const mustStartAfter = !isNil(mustStartAfterTime)
      ? getTimeWithOffset(timeZoneId, mustStartAfterTime)
      : null;
    const mustEndBefore = !isNil(mustEndBeforeTime)
      ? getTimeWithOffset(timeZoneId, mustEndBeforeTime)
      : null;
    const matchingPeril = perils?.perils?.find(
      (x: { code: string; description: string }) =>
        x.code === data?.$msg?.claim?.peril
    );

    const fullLocation = [
      data?.$msg?.claim?.lossLocation?.streetLine1,
      data?.$msg?.claim?.lossLocation?.streetLine2,
      data?.$msg?.claim?.lossLocation?.city,
      data?.$msg?.claim?.lossLocation?.state,
      data?.$msg?.claim?.lossLocation?.zip,
    ]
      .filter((x) => x)
      .join(', ');

    return {
      ...data,
      assignmentHandling: {
        ...data?.$msg?.assignmentHandling,
        SLO: 0,
        isStateLicenseRequired: true,
        lossTimeZone: timeZoneId,
        appointmentStart: startTime || '',
        appointmentEnd: endtime || '',
        mustStartAfterTime: mustStartAfter,
        mustEndBeforeTime: mustEndBefore,
        durationInMinutes: durationInMinutes,
      },
      attributes: [
        ...(data?.$msg?.attributes?.flatMap((attribute: BlobAttribute) => {
          if (attribute?.name) {
            return attribute.id;
          }
          return [];
        }) || []),
      ],
      claim: {
        ...data?.$msg?.claim,
        perilTitle: matchingPeril?.description,
        timeZone: data?.$msg?.claim?.lossLocation?.timeZoneId || 'unknown',
        locations: [
          {
            locationType: 'lossLocation',
            location: fullLocation,
          },
        ],
      },
      contact: {
        ...data?.$msg?.contact,
        primary: {
          ...data?.$msg?.contact?.primary,
          insuredName: data?.$msg?.contact?.primary?.name,
          notes: '',
        },
        secondary: {
          ...data?.$msg?.contact?.secondary,
          insuredName: data?.$msg?.contact?.secondary?.name,
          notes: '',
        },
      },
    };
  }, [data, perils]);

  React.useEffect(() => {
    reset(parseTaskDataForForm);
    if (status === 'success') {
      setIsLoading(false);
    }
  }, [data, reset, parseTaskDataForForm, status]);

  const handleReassign = ({
    resourceId,
    reasonCode,
    forceReassign,
  }: {
    resourceId: string;
    reasonCode: string;
    forceReassign: boolean;
  }) => {
    if (forceReassign && onForceReassign) {
      onForceReassign(resourceId, reasonCode);
    } else {
      const reassignFormBody: ReassignFormBody = {
        resourceId: resourceId,
        reasonCode: reasonCode,
        assignmentHandling: {
          appointmentStart:
            parseTaskDataForForm.assignmentHandling?.appointmentStart,
          appointmentEnd:
            parseTaskDataForForm.assignmentHandling?.appointmentEnd,
        },
        claim: {
          timeZone: parseTaskDataForForm.claim?.timeZone,
        },
      };

      onSave(reassignFormBody);
    }
  };

  const handleSave = () => {
    handleSubmit((values: any) => {
      reset({ ...values });
      return onSave(values);
    })();
  };

  const renderTitle = () => {
    if (taskId) {
      return status === 'loading' ? '' : data?.$msg?.claim?.insuredName || '--';
    }

    return 'Add a New Task';
  };

  const renderGeneralInformation = (props: {
    currentTab: number;
    control: Control<FieldValues>;
    errors: FieldErrors;
  }) => {
    return <GeneralInformation {...props} />;
  };

  const renderNotes = ({ currentTab }: { currentTab: number }) => {
    if (!taskId || notesTabIndex < 0) return null;
    return (
      <TabPanel value={currentTab} index={notesTabIndex}>
        {/* NotesPanel pads its own root (reliable inline style). The sibling
            tabs wrap content in a padded Box, but that sx padding is overridden
            in this fieldset/drawer context (see the timeline tab's 24px
            !important), so we rely on NotesPanel's self-padding instead of
            double-wrapping. */}
        <NotesPanel
          taskId={taskId}
          tenantId={tenantId}
          claimNumber={data?.$msg?.claim?.claimNumber}
        />
      </TabPanel>
    );
  };

  const renderTimeline = ({ currentTab }: { currentTab: number }) => {
    if (timelineTabIndex < 0) return null;
    return (
      <TabPanel
        value={currentTab}
        index={timelineTabIndex}
        data-testid="general-info-tab"
      >
        <Box sx={{ padding: '24px !important' }}>
          <TaskTimelineTab data={taskEvents} status={getTaskEventsStatus} />
        </Box>
      </TabPanel>
    );
  };

  const renderSupplementalData = ({ currentTab }: { currentTab: number }) => {
    if (supplementalTabIndex < 0) return null;
    return (
      data?.$msg?.claim.supplementalData &&
      data.$msg.claim.supplementalData.length > 0 && (
        <TabPanel value={currentTab} index={supplementalTabIndex}>
          <SupplementalDataTab data={data.$msg.claim.supplementalData} />
        </TabPanel>
      )
    );
  };

  const attributeOptions = () => {
    return data?.$msg?.attributes?.flatMap?.(
      (item: any): { title: string; value: string } | [] => {
        if (item.name) {
          return {
            title: `${item.name} - ${item.value}` || '',
            value: item.id,
          };
        }
        return [];
      }
    );
  };

  const renderTaskLock = () => (
    <FormControlLabel
      disabled={
        !canEdit ||
        data?.$metadata?.targetQueue === 'egress' ||
        data?.$metadata?.targetQueue === 'wip_q'
      }
      control={
        <Switch
          checked={
            data?.$msg?.lockedAt !== null && data?.$msg?.lockedAt !== undefined
          }
          onClick={onLockTask}
          size={'small'}
        />
      }
      label={'Locked'}
      labelPlacement={'start'}
    />
  );

  const renderAssignmentInformation = (props: {
    currentTab: number;
    control: Control<FieldValues>;
    errors: FieldErrors;
  }) => {
    return (
      <AssignmentInformation
        {...props}
        company={company}
        handleReassign={handleReassign}
        rescheduleReasons={rescheduleReasons}
        taskData={data}
        setValue={setValue}
        getValues={getValues}
        resourceData={data?.resource}
        resources={resources}
        permissions={permissions}
        resourceMeta={resourceMeta}
        attributes={attributes}
        inspectionOptions={[
          {
            title: data?.$msg?.assignmentHandling?.methodOfInspection || '',
            value: data?.$msg?.assignmentHandling?.methodOfInspection || '',
          },
        ]}
        perils={perils?.map((x: TaskPeril) => {
          return { title: x.description, value: x.code };
        })}
        renderTaskLock={renderTaskLock}
        isDirty={isDirty}
        assignedResource={assignedResource}
      />
    );
  };

  const showCancel = React.useMemo(() => {
    const canCancelTask = (targetQueue: string | undefined) => {
      const cancelTaskExclusionQueues: string[] = [
        'cancelled_q',
        'input_q',
        'egress',
      ];
      return (
        cancelTaskExclusionQueues?.find((queue) => queue === targetQueue) ===
        undefined
      );
    };

    return (
      (canEdit || isAdjuster) &&
      canCancelTask(data?.$msg?.assignmentHandling.targetQueue)
    );
  }, [permissions, data?.$msg?.assignmentHandling.targetQueue]);

  const [showCancelReasonModal, setShowCancelReasonModal] =
    React.useState(false);
  const renderCancelContent: React.ReactElement = (
    <Form<TaskCancelRequest>
      errors={formCancelState.errors}
      control={formCancelControl}
      handleSubmit={handleCancelSubmit}
      render={({ control, errors }: RenderFuncProps) => {
        return (
          <>
            <p>
              Do you really want to <b>Cancel?</b> You must select a reason
            </p>
            <Grid container spacing={1} alignItems="baseline">
              <Grid size={8}>
                {cancelReasonsStatus === 'error' && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mb: 1, display: 'block' }}
                  >
                    Failed to load cancel reasons
                  </Typography>
                )}
                <SelectField
                  id="cancelReason"
                  name="cancelReason"
                  label="Reason"
                  control={control}
                  errors={errors}
                  options={cancelReasons || []}
                />
              </Grid>
            </Grid>
          </>
        );
      }}
    />
  );

  const renderDeactivateModal = () => {
    return (
      <ConfirmModal
        actionStatus={cancelStatus}
        onConfirm={() => {
          const reason = watch('cancelReason') ?? '';
          onCancelTask(reason);
        }}
        onDeny={() => {
          resetCancel();
          setShowCancelReasonModal(false);
        }}
        confirmContent={renderCancelContent}
        confirmTitle={'Cancel Task'}
        modalType={'cancelTask'}
      />
    );
  };

  const currentQueue = React.useMemo(() => {
    return data?.$metadata?.targetQueue;
  }, [data?.$metadata?.targetQueue]);

  const isStarted = currentQueue === STARTED_QUEUE;
  const isComplete = currentQueue === COMPLETED_QUEUE;
  const isCancelled = currentQueue === CANCELLED_QUEUE;

  React.useEffect(() => {
    setShowCancelReasonModal(false);
  }, [currentQueue]);

  const handleCancelTask = () => {
    setShowCancelReasonModal(true);
  };

  const renderCancelButton = useMemo(
    () => (
      <Stack px={1}>
        <Button
          data-testid="cancel-task-button"
          type="button"
          variant="outlined"
          color="error"
          onClick={handleCancelTask}
          disabled={!showCancel || isCancelled || isLoading}
          sx={{
            fontWeight: '700',
            lineHeight: '1.3750em',
            letterSpacing: '0.029em',
          }}
        >
          Cancel Task
        </Button>
      </Stack>
    ),

    [showCancel, isCancelled, isLoading]
  );

  const taskStateFabButton = (
    title: string,
    icon: React.ReactElement,
    onClick: () => void,
    disabled: boolean
  ) => {
    return (
      <Stack
        data-testid="action-fab-button-container"
        direction={'row'}
        gap={1}
        alignItems="center"
        marginRight="4px"
      >
        <Typography
          variant="caption"
          fontSize="14px"
          boxShadow={
            !disabled
              ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
              : undefined
          }
          borderRadius="4px"
          sx={(theme) => ({
            backgroundColor: !disabled
              ? theme.palette.background.paper
              : theme.palette.mode === 'dark'
              ? theme.palette.action.disabledBackground
              : 'rgb(235, 235, 228)',
            padding: '2px 6px',
          })}
        >
          {title}
        </Typography>
        <Fab
          data-testid="action-fab-button"
          disabled={disabled}
          onClick={!disabled ? onClick : undefined}
          size="medium"
          sx={(theme) => ({
            '&.MuiFab-root': {
              backgroundColor: disabled
                ? theme.palette.mode === 'dark'
                  ? theme.palette.action.disabledBackground
                  : 'rgb(235, 235, 228)'
                : undefined,
            },
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'black',
            background: !disabled
              ? theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : grey[50]
              : theme.palette.mode === 'dark'
              ? theme.palette.action.disabledBackground
              : 'rgb(235, 235, 228)',
            '&:hover': {
              //you want this to be the same as the backgroundColor above
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.action.hover
                  : 'rgb(235, 235, 228)',
            },
          })}
          aria-label="add"
        >
          {icon}
        </Fab>
      </Stack>
    );
  };

  const canCancelClaimOwner = React.useMemo(
    () => !!permissions?.can('cancel', 'claim-owner'),
    [permissions]
  );

  const [showFabButtons, setShowFabButtons] = React.useState(false);
  const renderFabButton = React.useMemo(() => {
    return (
      <Stack
        data-testid="task-fab-button-container"
        onMouseLeave={() => setShowFabButtons(false)}
        alignItems="flex-end"
        sx={{
          position: 'absolute',
          bottom: 60,
          right: 20,
          gap: 2,
        }}
      >
        {showFabButtons && (
          <>
            {taskStateFabButton(
              'Start',
              <ContentPasteGoOutlinedIcon />,
              onStartTask,
              status === 'loading' || disableButtons || isStarted || isComplete
            )}
            {taskStateFabButton(
              'Complete',
              <AssignmentTurnedInOutlinedIcon />,
              onCompleteTask,
              status === 'loading' || disableButtons || isComplete || !isStarted
            )}
            {taskStateFabButton(
              'Cancel',
              <AssignmentLateOutlinedIcon />,
              handleCancelTask,
              !showCancel ||
                isCancelled ||
                isComplete ||
                (data?.$msg.taskType === TaskType.CLAIM_OWNER &&
                  !canCancelClaimOwner)
            )}
          </>
        )}

        <Fab
          data-testid="task-fab-button"
          disabled={isComplete || isCancelled}
          onClick={() => setShowFabButtons(!showFabButtons)}
          sx={{
            color: 'white',
            '&.MuiFab-root': {
              backgroundColor:
                isComplete || isCancelled ? 'rgb(235, 235, 228)' : indigo[700],
            },
          }}
          aria-label="add"
        >
          <ContentPasteIcon />
        </Fab>
      </Stack>
    );
  }, [showFabButtons, isComplete, isStarted, isCancelled, showCancel]);

  const cancelTaskButton = React.useMemo(() => {
    if (isAdjuster) return [];

    if (data?.$msg?.taskType === 'CLAIM_OWNER') {
      if (canCancelClaimOwner) {
        return renderCancelButton;
      }

      return [];
    }

    return renderCancelButton;
  }, [data?.$msg?.taskType, isAdjuster, isInternalAdmin, renderCancelButton]);

  const {
    setUnassignedTaskButton,
    showUnassignedTaskModal,
    renderUnassignedModal,
  } = useMarkUnassign({
    data,
    permissions,
    handleUnassignTask,
    isAdjuster,
    showCancel,
    isLoading,
    cancelStatus,
    canCancelClaimOwner,
  });

  const largeLossButton = () => {
    return (
      <Link to={`/complex-loss?claimNumber=${data?.$msg.claim.claimNumber}`}>
        <DrawerButton
          dataTestId="view-complex-loss-button"
          text="View Complex Loss"
          isDisabled={false}
          variant="outlined"
        />
      </Link>
    );
  };

  const renderLargeLossButton = React.useMemo(() => {
    const isLargeLoss =
      ['COMPLEX_CHILD', 'COMPLEX'].includes(data?.$msg.taskType || '') &&
      permissions?.can('access', 'complex-loss');
    // determine if task is part of a large loss to know if we should show the button.
    return isLargeLoss && largeLossButton();
  }, [data]);

  return (
    <TaskDetailsContext.Provider
      value={{
        value: showOnlyQualified?.value,
        setShowOnlyQualified: showOnlyQualified?.setShowOnlyQualified,
      }}
    >
      <DrawerComponent
        // the loading state from react query is ending before the fields are able to resolve
        // isLoading is used to control when the loading actually should finish.
        status={isLoading ? 'loading' : 'success'}
        onSave={handleSave}
        saveDisabled={isSaving ? true : !isDirty}
        showButtons={!!canEdit || isAdjuster}
        onClose={() => navigate(navigateToOnClose)}
        title={renderTitle()}
        showSaveButton={!!canEdit}
        renderLeftSideButtons={[
          featureFlags.naqSelfServiceEnabled &&
          isUnassignedNaqSurface(
            data?.$metadata?.targetQueue,
            data?.$msg?.assignedResource?.id
          ) &&
          !data?.$msg?.lockedAt &&
          permissions?.can('update', 'task') &&
          data?.id ? (
            <TaskRetryButton taskId={data.id} />
          ) : null,
          cancelTaskButton,
          setUnassignedTaskButton,
        ]}
        renderRightSideButtons={[renderLargeLossButton]}
        sx={{
          width: '65vw',
          '& .MuiBox-root': {
            padding: 0,
          },
        }}
        metas={[
          <DrawerMeta
            title="DATE OF LOSS"
            subtitle={
              data?.$msg?.claim.dateOfLoss
                ? format(new Date(data?.$msg?.claim.dateOfLoss), 'MM/dd/yyyy')
                : format(new Date(), 'MM/dd/yyyy')
            }
          />,
          <DrawerMeta
            title="CLAIM #"
            subtitle={data?.$msg?.claim?.claimNumber || '--'}
          />,
          <DrawerComponentMeta
            component={
              <Stack gap={2} direction="column">
                <Stack direction="row" gap={1} maxWidth={'10vw'}>
                  <GeoStatusChip geoStatus={geoStatus || ''} />
                  <TaskStatusChip
                    targetQueue={data?.$metadata?.targetQueue}
                    cancelledReason={data?.$msg?.status?.cancelled?.reason}
                    resourceId={data?.$msg?.assignedResource?.id}
                  />
                  <MethodOfInspectionChip
                    methodOfInspection={
                      data?.$msg?.assignmentHandling?.methodOfInspection || ''
                    }
                    variant="filled"
                    size="medium"
                  />
                </Stack>
                {/* The NAQ reason chip is scoped to needs_attention_q only --
                    latestNaqEvent / naqDiagnosis only exist for tasks that
                    actually entered the NAQ. The retry button (above) is the
                    part that surfaces on the broader unassigned set. */}
                {data?.$metadata?.targetQueue === 'needs_attention_q' && (
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <TaskNAQChip
                      message={
                        latestNaqEvent?.failureMessage || 'Unknown Error'
                      }
                      rootCauses={
                        featureFlags.naqSelfServiceEnabled
                          ? data?.$metadata?.naqDiagnosis?.rootCauses
                          : undefined
                      }
                    />
                  </Stack>
                )}
                {/* Own row: the first chip row is capped at 10vw. Not gated on
                    targetQueue -- the backend returns true only for an
                    unassigned, non-cancelled task. */}
                {data?.wasManuallyUnassigned === true && (
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <ManualUnassignmentChip variant="filled" size="medium" />
                  </Stack>
                )}
              </Stack>
            }
          />,
        ]}
        renderTabs={
          <TabContainer
            tabLabels={tabLabels}
            setTab={setTab}
            currentTab={currentTab}
          />
        }
        tabsRightSlot={
          data?.$metadata?.targetQueue === 'needs_attention_q' &&
          featureFlags.naqSelfServiceEnabled &&
          latestNaqEvent?.eventDateTime ? (
            <Box sx={{ color: 'common.white' }}>
              <TimeInNaqIndicator enteredAt={latestNaqEvent.eventDateTime} />
            </Box>
          ) : null
        }
        isActive={data?.$metadata?.targetQueue !== 'cancelled_q'}
      >
        <Form<Partial<TaskForm>>
          errors={formErrors}
          control={formControl}
          handleSubmit={handleSubmit}
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <StyledFieldSet
                disabled={
                  data?.$metadata?.targetQueue === 'cancelled_q'
                    ? true
                    : !canEdit
                }
              >
                {renderAssignmentInformation({
                  currentTab: currentTab,
                  control,
                  errors,
                })}
                {renderGeneralInformation({
                  currentTab: currentTab,
                  control,
                  errors,
                })}
                {renderTimeline({ currentTab: currentTab })}
                {renderSupplementalData({ currentTab: currentTab })}
              </StyledFieldSet>
            );
          }}
        />
        {/* NOTES is rendered OUTSIDE the task-edit <StyledFieldSet> on purpose.
            That fieldset is `disabled` when the user can't edit the task — and
            for COMPLEX/COMPLEX_CHILD tasks `canEdit` requires `complex-loss`
            upsert. A disabled <fieldset> cascades to every descendant control,
            which would silently disable Add-note, the review toggle, and the
            related-notes link. Notes are independently permissioned
            (manage:notes / read:notes) and must stay interactive regardless of
            task-edit rights. The panel binds no form fields and TabPanel
            shows/hides by index, so rendering it here (outside the form) is
            safe. Do NOT move it back inside the fieldset. */}
        {renderNotes({ currentTab: currentTab })}
        {isAdjuster && renderFabButton}
      </DrawerComponent>
      {showCancelReasonModal && renderDeactivateModal()}
      {showUnassignedTaskModal && renderUnassignedModal()}
    </TaskDetailsContext.Provider>
  );
}

export const TaskStatusChip = (props: {
  targetQueue?: string;
  cancelledReason?: string;
  resourceId?: string;
}) => {
  const { cancelReasons } = useCompany();

  if (props.targetQueue === 'cancelled_q') {
    // Prefer tenant-configured cancel reasons (title === value),
    // fall back to legacy camelCase mappings, then raw value.
    let cancelReasonTitle: string | undefined;

    if (props.cancelledReason) {
      const found = cancelReasons?.find(
        (r) =>
          r.value === props.cancelledReason || r.title === props.cancelledReason
      );
      if (found) {
        cancelReasonTitle = found.title;
      }
    }

    if (!cancelReasonTitle) {
      switch (props.cancelledReason) {
        case 'coverageDenied':
          cancelReasonTitle = 'Coverage Denied';
          break;
        case 'alternateMoiUsed':
          cancelReasonTitle = 'Alternate MOI used';
          break;
        case 'claimWithdrawal':
          cancelReasonTitle = 'Claim Withdrawal/Under Deductible';
          break;
        case 'customerNotAvailable':
          cancelReasonTitle = 'Customer/Vendor not available';
          break;
        case 'weather':
          cancelReasonTitle = 'Weather';
          break;
        default:
          cancelReasonTitle = props.cancelledReason || 'Other';
          break;
      }
    }

    return (
      <Chip
        label={`Cancelled - ${cancelReasonTitle}`}
        color="error"
        size="medium"
        data-testid="task-status"
        title={`Cancelled - ${cancelReasonTitle}`}
      />
    );
  }

  return (
    <TaskAssignedChip
      resourceId={props.resourceId}
      targetQueue={props.targetQueue || ''}
    />
  );
};
